import { Marked } from 'marked';
import type { Tokens } from 'marked';

const escapeHtml = (value: string) => value.replace(/[&<>"']/g, character =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]!);

export function websiteSourceUrl(value: string): string | null {
  try {
    if (!/^https?:\/\//i.test(value) || /[\s<>"'`]/.test(value)) return null;
    const url = new URL(value);
    return url.username || url.password ? null : url.href;
  } catch { return null; }
}

// Keep the renderer local to this component; do not change the global chat renderer.
// Angular also sanitizes the resulting HTML at the binding (no trust bypass).
const markdown = new Marked({ gfm: true, breaks: true, renderer: {
  html: ({ text }) => escapeHtml(text),
  link({ href, tokens }) {
    const label = this.parser.parseInline(tokens);
    const url = websiteSourceUrl(href);
    return url ? `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${label}</a>` : label;
  },
  image: ({ text }) => escapeHtml(text),
  codespan: ({ text }) => {
    const url = websiteSourceUrl(text);
    return url ? `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(text)}</a>`
      : `<code>${escapeHtml(text)}</code>`;
  },
} });

export const reviewMarkdown = (value: string): string => markdown.parse(value) as string;

// Remove only a standalone introduction, never findings or qualifications in the same paragraph.
const withoutReportPreamble = (content: string): string => content.replace(
  /^\s*(?:here['’]s|here is) a \*{0,2}keyword-summary overview\*{0,2} of the selected legacy website report for \*{0,2}https?:\/\/[^\s]+?\*{0,2}:\s*(?:\r?\n\s*\r?\n|$)/i, '',
).replace(
  /^\s*(?:here['’]s|here is) (?:a |an )?(?:(?:short|brief|read-only)[, ]+)*overview of (?:the |this )?(?:selected |current )?website report\s*[:.]\s*(?:\r?\n\s*\r?\n|$)/i,
  '',
);

/** A report action remains a report even when the model changes its prose format. */
export function presentWebsiteReport(content: string, requested = false, metadata: ReportDisplayMetadata = {}): WebsiteReview | null {
  if (!requested) return null;
  const structured = parseWebsiteReview(content);
  if (structured) return distinctReportContent(structured, metadata);
  if (!content.trim()) return null;
  const displayContent = withoutReportPreamble(content);
  const blocks = markdown.lexer(displayContent).filter(token => token.type !== 'space');
  const firstList = blocks.find(token => token.type === 'list') as Tokens.List | undefined;
  const paragraphs = blocks.filter(token => token.type === 'paragraph');
  const plain = (text: string) => text.replace(/[*_]/g, '').trim();
  const nextHeading = /^(?:(?:one useful|suggested|recommended) )?next steps?$|^proximos? passos?$/i;
  const findingsHeading = /^(?:key |main )?findings$|^(?:quick read|summary|overview|resultados|principais resultados)$/i;
  const limitationHeading = /^(?:limitations?|caveats?|evidence limits?|source limitations?)$/i;
  const sections: {title: string; parts: string[]}[] = [];
  for (const block of blocks) {
    const label = plain(block.raw).replace(/:$/, '');
    const heading = block.type === 'heading' ? block.text : block.type === 'paragraph'
      ? block.raw.trim().match(/^\*\*([^*\n]+)\*\*\s*$/)?.[1]
        ?? ((findingsHeading.test(label) || nextHeading.test(label) || limitationHeading.test(label)) ? label : undefined) : undefined;
    if (heading) sections.push({title:plain(heading).replace(/:$/, ''),parts:[]});
    else if (sections.length) sections[sections.length-1].parts.push(block.raw);
  }
  const findings = sections.find(section => findingsHeading.test(section.title));
  const nextSection = sections.find(section => nextHeading.test(section.title));
  const limitations = sections.filter(section => limitationHeading.test(section.title)).flatMap(section => section.parts);
  const compactFindings = findings ? markdown.lexer(findings.parts.join('\n\n')).map(block => {
    if (block.type !== 'list') return block.raw;
    const items = (block as Tokens.List).items.filter(item => !item.tokens.some(token => token.type === 'list'));
    return items.filter((item, index) => index < 3 || /\b(not confirmed|incomplete|partial|unavailable|may|only|limitation)\b/i.test(item.text)).map(item => item.raw).join('\n');
  }).join('\n\n') : '';
  const next = paragraphs.find(token => /^(?:(?:one useful|suggested|recommended) )?next steps?\s*:/i.test(plain(token.raw)));
  const summaryParts = firstList
    ? [firstList.items.filter(item => !item.tokens.some(token => token.type === 'list')).slice(0, 3).map(item => item.raw).join('\n'),
       ...paragraphs.filter(token => token !== next && !/^Report:\s*\/dashboard\/website-scan\/(?:legacy\/)?[\w-]+\s*$/i.test(token.raw.trim())).map(token => token.raw)]
    : blocks.filter(token => token !== next).slice(0, 2).map(token => token.raw);
  const sourceUrls: string[] = [];
  markdown.walkTokens(markdown.lexer(content), token => {
    const candidate = token.type === 'link' ? token.href : token.type === 'codespan' ? token.text : '';
    const sourceUrl = websiteSourceUrl(candidate);
    if (sourceUrl) sourceUrls.push(sourceUrl);
  });
  const urls = [...new Set(sourceUrls)];
  const url = urls.length === 1 ? urls[0] : '';
  const summarySource = [compactFindings || summaryParts.join('\n\n'), ...limitations].join('\n\n');
  const nextStepSource = nextSection ? nextSection.parts.join('\n\n') : next ? next.raw.replace(/^(?:\*\*)?(?:(?:one useful|suggested|recommended) )?next steps?\s*:(?:\*\*)?\s*/i, '') : '';
  return distinctReportContent({ url, hostname: url ? new URL(url).hostname.replace(/^www\./, '') : '', intro: '', sourceNote: '',
    highlights: [], summary: reviewMarkdown(summarySource), nextStep: reviewMarkdown(nextStepSource), summarySource, nextStepSource,
    sections: [{ title: '', body: content, html: reviewMarkdown(displayContent) }] }, metadata);
}

export interface ReportDisplayMetadata { name?: string; scanDate?: string; path?: string; }
const contentKey = (html: string) => html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

/** Assign each complete block to one place. Keep raw model text and substantive qualifications intact. */
function distinctReportContent(report: WebsiteReview, metadata: ReportDisplayMetadata): WebsiteReview {
  const seen = new Set<string>();
  const isMetadata = (source: string) => {
    const text = contentKey(reviewMarkdown(source));
    const url = text.replace(/^(?:URL|Scanned page|Website URL):\s*/i, '');
    if (report.url && websiteSourceUrl(url.replace(/&amp;/g, '&')) === report.url) return true;
    if (metadata.path && text.replace(/^Report:\s*/i, '') === metadata.path) return true;
    const name = text.match(/^(?:(?:Your|The) (?:active|selected|current) website report is|Report name:)\s+(.+?)\.?$/i)?.[1];
    if (name && name.trim() === escapeHtml(metadata.name ?? '')) return true;
    const date = text.match(/^Scan date:\s*(.+)$/i)?.[1];
    return !!date && !!metadata.scanDate && Number.isFinite(Date.parse(date)) && Number.isFinite(Date.parse(metadata.scanDate))
      && new Date(date).toISOString().slice(0, 10) === new Date(metadata.scanDate).toISOString().slice(0, 10);
  };
  const filter = (source: string): string => {
    const output: string[] = [];
    let heading = '';
    for (const block of markdown.lexer(withoutReportPreamble(source))) {
      if (block.type === 'space') continue;
      const label = contentKey(reviewMarkdown(block.raw)).replace(/:$/, '');
      if (block.type === 'heading' || /^(?:findings|key findings|main findings|summary|overview|next steps?|suggested next steps?|limitations?)$/i.test(label)) {
        heading = block.raw;
        continue;
      }
      const candidates = block.type === 'list' ? (block as Tokens.List).items.map(item => item.raw) : [block.raw];
      const kept = candidates.map(raw => {
        // Only remove standalone metadata lines; never remove a URL embedded in a finding.
        const cleaned = raw.split('\n').filter(line => !isMetadata(line.replace(/^\s*(?:[-*+] |\d+\. )/, ''))).join('\n').trim();
        const key = contentKey(reviewMarkdown(cleaned));
        if (!key || seen.has(key)) return '';
        seen.add(key);
        return cleaned;
      }).filter(Boolean);
      if (kept.length) {
        if (heading) output.push(heading);
        output.push(kept.join('\n'));
      }
      heading = '';
    }
    const result = output.join('\n\n');
    if (result) seen.add(contentKey(reviewMarkdown(result)));
    return result;
  };
  const summary = reviewMarkdown(filter(report.summarySource ?? ''));
  const nextStep = reviewMarkdown(filter(report.nextStepSource ?? ''));
  const intro = reviewMarkdown(filter(report.introSource ?? ''));
  const sourceNote = reviewMarkdown(filter(report.sourceNoteSource ?? ''));
  const highlights = report.highlights.filter(item => {
    const key = contentKey(item.html);
    if (seen.has(key)) return false;
    seen.add(key); return true;
  });
  const sections = report.sections.map(section => ({...section, html:reviewMarkdown(filter(section.body))})).filter(section => section.html.trim());
  return {...report, summary, nextStep, intro, sourceNote, highlights, sections};
}

export interface WebsiteReviewSection { title: string; body: string; html: string; }
export interface WebsiteReview {
  url: string;
  hostname: string;
  intro: string;
  sourceNote: string;
  sections: WebsiteReviewSection[];
  highlights: { value: string; label: string; html: string }[];
  summary: string;
  nextStep: string;
  summarySource?: string;
  nextStepSource?: string;
  introSource?: string;
  sourceNoteSource?: string;
}

/** Enhance only a recognizable, single-page review. Unknown formats keep normal chat rendering. */
export function parseWebsiteReview(content: string): WebsiteReview | null {
  const sections: WebsiteReviewSection[] = [];
  let intro = '';
  for (const token of markdown.lexer(withoutReportPreamble(content))) {
    const boldHeading = token.type === 'paragraph' ? token.raw.match(/^\*\*([^*\n]+)\*\*[ \t]*(?:\r?\n|$)/) : null;
    const heading = token.type === 'heading' ? token.text : boldHeading?.[1];
    if (heading) sections.push({ title: heading, body: boldHeading ? token.raw.slice(boldHeading[0].length) : '', html: '' });
    else if (sections.length) sections[sections.length - 1].body += token.raw;
    else intro += token.raw;
  }
  const sources = sections.filter(section => /^scanned page$/i.test(section.title));
  if (sources.length !== 1 || sections.length < 3) return null;
  const source = sources[0];
  const lines = source.body.trim().split('\n');
  const candidate = lines[0]?.trim().replace(/^[-*]\s+/, '');
  const rawUrl = candidate?.match(/^`([^`]+)`$/)?.[1]
    ?? candidate?.match(/^\[[^\]]+\]\((https?:\/\/[^)]+)\)$/)?.[1] ?? candidate;
  const url = websiteSourceUrl(rawUrl ?? '');
  // More than one source needs a multi-page UI, not a misleading single-page card.
  if (!url || /https?:\/\//i.test(lines.slice(1).join('\n'))) return null;
  const overview = sections.find(section => /^scan overview$/i.test(section.title));
  const overviewTokens = overview ? markdown.lexer(overview.body).filter(token => token.type !== 'space') : [];
  const list: Tokens.List | null = overviewTokens.length === 1 && overviewTokens[0].type === 'list' ? overviewTokens[0] as Tokens.List : null;
  const highlights = list && list.items.length <= 6 ? list.items.map(item => {
    const plain = item.text.replace(/\*\*/g, '');
    const numeric = plain.match(/^(\d[\d,]*)\s+(.+)$/);
    const high = /^All detected findings were High$/i.test(plain);
    return { value: numeric?.[1] ?? (high ? 'High' : ''),
      label: numeric?.[2] ?? (high ? 'All detected findings' : ''), html: reviewMarkdown(item.text) };
  }) : [];
  const quickRead = sections.find(section => /^quick read$/i.test(section.title));
  const nextSteps = sections.find(section => /^suggested next steps$/i.test(section.title));
  const firstStepBlock = nextSteps ? markdown.lexer(nextSteps.body).find(token => token.type !== 'space') : null;
  // Keep the complete first recommendation, including nested qualifications. Never clip a sentence.
  const nextStep = firstStepBlock?.type === 'list' ? reviewMarkdown((firstStepBlock as Tokens.List).items[0]?.text ?? '') : '';
  return { url, hostname: new URL(url).hostname.replace(/^www\./, ''), intro: reviewMarkdown(intro),
    summary: quickRead ? reviewMarkdown(quickRead.body) : reviewMarkdown(intro), nextStep,
    summarySource: quickRead?.body ?? intro,
    nextStepSource: firstStepBlock?.type === 'list' ? (firstStepBlock as Tokens.List).items[0]?.text ?? '' : '',
    introSource: intro, sourceNoteSource: lines.slice(1).join('\n'),
    sourceNote: reviewMarkdown(lines.slice(1).join('\n')),
    sections: sections.filter(section => section !== source && !(section === overview && highlights.length))
      .map(section => ({ ...section, html: reviewMarkdown(section.body) })), highlights };
}
